import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import OneTimeSecretModal from '../Components/OneTimeSecretModal';
import NotAvailable from '../Components/NotAvailable';
import {
    CLIENT_NAME_RULE,
    DEFAULT_SCOPES,
    getClient,
    invalidScopes,
    isValidClientName,
    isWhitelistError,
    parseScopes,
    redirectUriError,
    registerClient,
    sameItems,
    updateClient,
    useNonMmlAdminAccess,
} from '../utils/oidcClients';
import {
    MAX_LIMIT, idOf, inquiryOptionLabel, listPartnerInquiries, statusLabel,
} from '../utils/partnerInquiries';

// admin#175 — register a partner client (/oidcClientsAdd) or edit one
// (/oidcClientsAdd/:clientId). Edit sends only the fields that changed, so a
// client this form doesn't fully model (somo-cli's SMAPI scopes, a machine
// client with no redirect URIs) is never rewritten by accident. For the same
// reason, edit validates a field only when it changed, as the API does.
function OidcClientsAdd() {
    const { clientId } = useParams();
    const isEdit = Boolean(clientId);
    const navigate = useNavigate();
    const allowed = useNonMmlAdminAccess();

    const [original, setOriginal] = useState(null);
    const [name, setName] = useState('');
    const [redirectUris, setRedirectUris] = useState(['']);
    const [scopesText, setScopesText] = useState(DEFAULT_SCOPES.join(' '));
    // admin#181 — the partner inquiry this client is for; '' for none
    // (GoalTide, SomoCares and Pulse came from no inquiry).
    const [partnerContactId, setPartnerContactId] = useState('');
    const [inquiries, setInquiries] = useState([]);
    const [inquiriesError, setInquiriesError] = useState('');
    const [errors, setErrors] = useState({});
    const [whitelistBlocked, setWhitelistBlocked] = useState(false);
    const [loading, setLoading] = useState(isEdit);
    const [loadError, setLoadError] = useState('');
    const [saving, setSaving] = useState(false);
    // { clientId, clientSecret } — in state only, cleared when the modal closes.
    const [issued, setIssued] = useState(null);

    useEffect(() => {
        if (!isEdit || !allowed) return;
        (async () => {
            try {
                const client = await getClient(clientId);
                setOriginal(client);
                setName(client.name || '');
                setRedirectUris(client.redirectUris?.length ? client.redirectUris : ['']);
                setScopesText((client.allowedScopes || []).join(' '));
                setPartnerContactId(idOf(client.partnerContactId));
            } catch (err) {
                setLoadError(err.message || 'Failed to load client.');
            } finally {
                setLoading(false);
            }
        })();
    }, [isEdit, clientId, allowed]);

    // A failure here only disables the picker; the rest of the form works.
    useEffect(() => {
        if (!allowed) return;
        listPartnerInquiries({ limit: MAX_LIMIT })
            .then((result) => setInquiries(result?.contacts || []))
            .catch((err) => setInquiriesError(err.message || 'Failed to load partner inquiries.'));
    }, [allowed]);

    const setUri = (index, value) => setRedirectUris((uris) => uris.map((u, i) => (i === index ? value : u)));
    const addUri = () => setRedirectUris((uris) => [...uris, '']);
    const removeUri = (index) => setRedirectUris((uris) => uris.filter((_, i) => i !== index));

    const validate = (uris, scopes) => {
        const next = {};
        const nameChanged = !isEdit || name !== original?.name;
        if (nameChanged && !isValidClientName(name)) next.name = CLIENT_NAME_RULE;

        const requireUri = !isEdit || (original?.redirectUris?.length ?? 0) > 0;
        if (requireUri && uris.length === 0) next.redirectUris = 'Add at least one redirect URI.';
        // An unchanged list isn't sent, so a stored URI this form wouldn't
        // accept (a seeded http:// one, say) mustn't block an unrelated edit.
        if (!isEdit || !sameItems(uris, original?.redirectUris || [])) {
            uris.forEach((u, i) => {
                const msg = redirectUriError(u);
                if (msg) next[`uri-${i}`] = msg;
            });
        }

        if (scopes.length === 0) {
            next.scopes = 'Add at least one scope.';
        } else if (!isEdit || !sameItems(scopes, original?.allowedScopes || [])) {
            const bad = invalidScopes(scopes);
            if (bad.length > 0) next.scopes = `Invalid scope: ${bad.join(', ')}. Use letters, digits and _ : . - / only.`;
        }
        return next;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // The form stays mounted under the one-time secret modal, and the modal
        // doesn't stop the keyboard: Enter would register a second client.
        if (issued || saving) return;
        setWhitelistBlocked(false);
        const uris = redirectUris.map((u) => u.trim()).filter(Boolean);
        const scopes = parseScopes(scopesText);
        const found = validate(uris, scopes);
        setErrors(found);
        if (Object.keys(found).length > 0) return;

        setSaving(true);
        try {
            if (isEdit) {
                const changes = {};
                if (name.trim() !== original.name) changes.name = name.trim();
                if (!sameItems(uris, original.redirectUris || [])) changes.redirectUris = uris;
                if (!sameItems(scopes, original.allowedScopes || [])) changes.allowedScopes = scopes;
                // null clears the link (api#766).
                if (partnerContactId !== idOf(original.partnerContactId)) {
                    changes.partnerContactId = partnerContactId || null;
                }
                if (Object.keys(changes).length === 0) {
                    toast.info('Nothing changed.');
                    return;
                }
                await updateClient(clientId, changes);
                toast.success('Client updated. Changes apply within about a minute.');
                navigate('/oidcClients');
            } else {
                const payload = { name: name.trim(), redirectUris: uris, allowedScopes: scopes };
                if (partnerContactId) payload.partnerContactId = partnerContactId;
                const result = await registerClient(payload);
                setIssued({ clientId: result.clientId, clientSecret: result.clientSecret });
            }
        } catch (err) {
            if (isWhitelistError(err.message)) setWhitelistBlocked(true);
            toast.error(err.message || 'Save failed.');
        } finally {
            setSaving(false);
        }
    };

    if (allowed === false) return <NotAvailable />;

    const revoked = original?.status === 'revoked';

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 txt_lft">{isEdit ? 'Edit Partner Client' : 'Register Partner Client'}</h1>
                        </div>
                        <div className="col-sm-6 txt_rgt">
                            <Link to="/oidcClients" className="btn btn-secondary">Back</Link>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="card mb-3">
                        <div className="card-body txt_lft">
                            {loading && (
                                <div className="text-center p-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            )}
                            {!loading && revoked && (
                                <div className="alert alert-secondary">
                                    This client is revoked and can no longer be edited. Register a new client instead.
                                </div>
                            )}
                            {!loading && loadError && (
                                <div className="alert alert-danger" role="alert">
                                    Couldn&apos;t load this client: {loadError}{' '}
                                    <Link to="/oidcClients">Back to Partner Clients</Link>
                                </div>
                            )}
                            {!loading && !loadError && !revoked && (
                                <form onSubmit={handleSubmit} noValidate>
                                    {isEdit && (
                                        <p className="text-muted">
                                            Client ID: <code>{clientId}</code> (doesn&apos;t change when you rename).
                                        </p>
                                    )}

                                    <div className="form-group">
                                        <label htmlFor="oidc-name" className="fwt_600">Name</label>
                                        <input
                                            id="oidc-name"
                                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            maxLength={100}
                                        />
                                        <small className="form-text text-muted">
                                            Letters, digits, spaces, hyphens and underscores; must start with a
                                            letter or digit. Used to build the client ID.
                                        </small>
                                        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                    </div>

                                    <div className="form-group">
                                        <span className="fwt_600 d-block mb-1">Redirect URIs</span>
                                        {redirectUris.map((uri, i) => (
                                            // eslint-disable-next-line react/no-array-index-key
                                            <div className="mb-2" key={i}>
                                                <div className="input-group">
                                                    <input
                                                        aria-label={`Redirect URI ${i + 1}`}
                                                        className={`form-control ${errors[`uri-${i}`] ? 'is-invalid' : ''}`}
                                                        value={uri}
                                                        placeholder="https://partner.example.com/callback"
                                                        onChange={(e) => setUri(i, e.target.value)}
                                                    />
                                                    {redirectUris.length > 1 && (
                                                        <div className="input-group-append">
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary"
                                                                onClick={() => removeUri(i)}
                                                                aria-label={`Remove redirect URI ${i + 1}`}
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                    )}
                                                    {errors[`uri-${i}`] && (
                                                        <div className="invalid-feedback">{errors[`uri-${i}`]}</div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                        {errors.redirectUris && <div className="text-danger f_sz13">{errors.redirectUris}</div>}
                                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={addUri}>
                                            Add redirect URI
                                        </button>
                                    </div>

                                    {whitelistBlocked && (
                                        <div className="alert alert-warning" role="alert">
                                            This domain isn&apos;t allowed yet. Add it to <code>ALLOWED_ORIGINS</code> in
                                            AWS Secrets Manager for this environment, then register again.
                                        </div>
                                    )}

                                    <div className="form-group">
                                        <label htmlFor="oidc-scopes" className="fwt_600">Scopes</label>
                                        <input
                                            id="oidc-scopes"
                                            className={`form-control ${errors.scopes ? 'is-invalid' : ''}`}
                                            value={scopesText}
                                            onChange={(e) => setScopesText(e.target.value)}
                                        />
                                        <small className="form-text text-muted">
                                            Space-separated. Partners normally need <code>openid profile email</code>.
                                        </small>
                                        {errors.scopes && <div className="invalid-feedback">{errors.scopes}</div>}
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="oidc-partner-inquiry" className="fwt_600">Partner inquiry</label>
                                        <select
                                            id="oidc-partner-inquiry"
                                            className="form-control"
                                            value={partnerContactId}
                                            onChange={(e) => setPartnerContactId(e.target.value)}
                                        >
                                            <option value="">None</option>
                                            {/* A linked inquiry outside the newest MAX_LIMIT still shows. */}
                                            {partnerContactId && !inquiries.some((c) => c._id === partnerContactId) && (
                                                <option value={partnerContactId}>Linked inquiry ({partnerContactId})</option>
                                            )}
                                            {inquiries.map((contact) => (
                                                <option key={contact._id} value={contact._id}>
                                                    {inquiryOptionLabel(contact)} · {statusLabel(contact.status)}
                                                </option>
                                            ))}
                                        </select>
                                        <small className="form-text text-muted">
                                            Optional. The /partners inquiry this client was registered for.
                                        </small>
                                        {inquiriesError && (
                                            <small className="form-text text-danger">
                                                Couldn&apos;t load partner inquiries: {inquiriesError}
                                            </small>
                                        )}
                                    </div>

                                    <button type="submit" className="btn btn-blue" disabled={saving || Boolean(issued)}>
                                        {isEdit ? 'Save changes' : 'Register client'}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {issued && (
                <OneTimeSecretModal
                    title="Client registered"
                    clientId={issued.clientId}
                    clientSecret={issued.clientSecret}
                    onClose={() => {
                        setIssued(null);
                        navigate('/oidcClients');
                    }}
                />
            )}
        </>
    );
}

export default OidcClientsAdd;
